import axios from 'axios';
import { map } from 'lodash';
import { type Logger } from 'pino';

const extractResponseError = async (e: unknown): Promise<{ status: number; message: string }> => {
  if (!e) return { status: 0, message: 'Unknown error' };

  if (Array.isArray(e)) return extractResponseError(e[0]);
  if (typeof e === 'string') return { status: 0, message: e };

  // Axios error
  if (axios.isAxiosError(e)) {
    const status = e.status ?? e.response?.status;
    const data = e.response?.data;
    let backendMessage;

    if (
      Array.isArray(data)
      && data.length > 0
      && typeof data[0] === 'object'
      && data[0] !== null
      && 'message' in data[0]
    )
      backendMessage = (data[0] as { message?: string }).message;
    else if (typeof data === 'object' && data !== null && 'message' in data)
      backendMessage = (data as { message?: string }).message;

    const message =
      (typeof backendMessage === 'string' && backendMessage)
      || (typeof data === 'string' && data)
      || e.message
      || 'Unknown error';

    return { status: status ?? 0, message };
  }

  // Fetch Response error
  if (e instanceof Response) {
    let message;
    try {
      const contentType = e.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const body = await e.json();
        if (
          body
          && typeof body === 'object'
          && 'message' in body
          && typeof body.message === 'string'
        )
          message = body.message;
        else if (body) message = JSON.stringify(body);
      } else {
        const text = await e.text();
        if (text) message = text;
      }
    } catch {
      // ignore body parse errors, fallback below
    }
    if (!message) message = e.statusText || `HTTP error ${e.status}`;

    return {
      status: e.status,
      message,
    };
  }

  if (typeof e === 'object' && e !== null) {
    let status;
    let message;

    if (
      'response' in e
      && e.response !== null
      && typeof e.response === 'object'
      && 'status' in e.response
      && typeof e.response.status === 'number'
    )
      status = e.response.status;
    else if ('status' in e && typeof e.status === 'number') status = e.status;

    if ('message' in e && typeof e.message === 'string') message = e.message;

    if (status !== undefined || message !== undefined)
      return { status: status ?? 0, message: message ?? 'Unknown error' };
  }

  return { status: 0, message: 'Unknown error' };
};

const logApiError = async ({
  error,
  endpoint,
  logger,
  allowedErrors = [],
  should40XError = false,
  parameters,
}: {
  error: unknown;
  endpoint: string;
  logger: Logger<never>;
  allowedErrors?: number[];
  should40XError?: boolean;
  parameters?: Record<string, string>;
}) => {
  const { status, message } = await extractResponseError(error);
  const reason = status ? `${status} - ${message}` : message;
  const timeouts = ['timeout', 'canceled', 'network error', 'request aborted'];

  // Request timeouts indicate an issue and should be logged but they are at warning level as many
  // client browser conditions can cause them. Failed requests will still get logged as errors in
  // the backend which will trip alarms if there is an issue in our services.
  if ([504].includes(status)) {
    logger.fatal(errorMessage(`Gateway timeout exception for ${endpoint}`, reason, parameters));
    return true;
  } else if (timeouts.includes(message.toLowerCase())) {
    logger.warn(errorMessage(`Network operation aborted for ${endpoint}`, reason, parameters));
    return true;
  } else if (allowedErrors.includes(status)) {
    logger.info(
      errorMessage(`Received allowed response code ${status} from ${endpoint}`, reason, parameters)
    );
    return false;
  } else if (!should40XError && [403, 404].includes(status)) {
    logger.warn(
      errorMessage(`Received ${status} response code from ${endpoint}`, reason, parameters)
    );
    return false;
  }
  logger.error(errorMessage(`Failed to get a response from ${endpoint}`, reason, parameters));
  return true;
};

const appendError = (action: string, e: unknown) => {
  let errorReason = 'Unknown reason';
  if (e instanceof Error) errorReason = e.message;
  return errorMessage(action, errorReason);
};

const errorMessage = (action: string, reason: string, parameters?: Record<string, string>) => {
  return `${action}.${parameters ? ` Parameters: ${map(parameters, (value, key) => `${key}=${value}`).join(' ')}.` : ''} Reason: ${reason}`;
};

export { appendError, errorMessage, extractResponseError, logApiError };
